/**
 * Client Activity Report — filters, summary cards, grid, CSV export.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  Link,
  Avatar,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataGrid, GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { enhancedApi } from '../../api';

dayjs.extend(relativeTime);

const cellVal = (p) => {
  if (p == null) return p;
  if (typeof p === 'object' && 'value' in p) return p.value;
  return p;
};

const fmtMoney = (n) =>
  n != null && !Number.isNaN(n)
    ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const initials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return `${p[0][0]}${p[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const healthChipProps = (score) => {
  if (score == null || Number.isNaN(score)) return { label: '—', color: 'default', variant: 'outlined' };
  const s = Number(score);
  if (s >= 70) return { label: 'Good', color: 'success' };
  if (s >= 40) return { label: 'Fair', color: 'warning' };
  return { label: 'Poor', color: 'error' };
};

const avgHealthChipProps = (avg) => {
  if (avg == null || Number.isNaN(avg)) return { label: '—', color: 'default', variant: 'outlined' };
  const base = healthChipProps(avg);
  return { ...base, label: `${base.label} (${avg.toFixed(0)})` };
};

function normalizeRows(data) {
  const raw = data?.rows ?? data?.results ?? data?.clients ?? [];
  return raw.map((r, i) => ({
    id: r.client_id ?? r.id ?? i,
    client_id: r.client_id ?? r.id,
    client_name: r.client_name ?? r.display_name ?? r.name ?? '—',
    client_type: r.client_type ?? r.type ?? 'Individual',
    tasks_done: Number(r.tasks_done ?? r.tasks_completed ?? 0),
    tasks_pending: Number(r.tasks_pending ?? 0),
    tasks_overdue: Number(r.tasks_overdue ?? 0),
    messages: Number(r.messages ?? r.messages_count ?? 0),
    files: Number(r.files ?? r.files_uploaded ?? r.files_count ?? 0),
    hours: Number(r.hours ?? 0),
    revenue: Number(r.revenue ?? r.revenue_generated ?? 0),
    last_activity_at: r.last_activity_at ?? r.last_activity ?? null,
    health_score: r.health_score != null ? Number(r.health_score) : null,
  }));
}

function normalizeSummary(data, rows) {
  const s = data?.summary ?? {};
  const fromApi = {
    total_clients: s.total_clients != null ? Number(s.total_clients) : null,
    tasks_completed: s.tasks_completed != null ? Number(s.tasks_completed) : null,
    revenue_generated: s.revenue_generated != null ? Number(s.revenue_generated) : null,
    files_uploaded: s.files_uploaded != null ? Number(s.files_uploaded) : null,
    avg_health_score: s.avg_health_score != null ? Number(s.avg_health_score) : null,
  };
  if (rows.length === 0) return fromApi;
  const sum = (f) => rows.reduce((a, r) => a + (Number(r[f]) || 0), 0);
  const healthVals = rows.map((r) => r.health_score).filter((v) => v != null && !Number.isNaN(v));
  const avgHealth = healthVals.length ? healthVals.reduce((a, b) => a + b, 0) / healthVals.length : null;
  return {
    total_clients: fromApi.total_clients ?? rows.length,
    tasks_completed: fromApi.tasks_completed ?? sum('tasks_done'),
    revenue_generated: fromApi.revenue_generated ?? sum('revenue'),
    files_uploaded: fromApi.files_uploaded ?? sum('files'),
    avg_health_score: fromApi.avg_health_score ?? avgHealth,
  };
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ReportTotalsFooter({ rows }) {
  const totals = useMemo(() => {
    const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    return {
      tasks_done: sum('tasks_done'),
      tasks_pending: sum('tasks_pending'),
      tasks_overdue: sum('tasks_overdue'),
      messages: sum('messages'),
      files: sum('files'),
      hours: sum('hours'),
      revenue: sum('revenue'),
    };
  }, [rows]);

  return (
    <GridFooterContainer
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        px: 1.5,
        py: 1,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Totals
        </Typography>
        <Typography variant="caption">
          Tasks done: <strong>{totals.tasks_done}</strong>
        </Typography>
        <Typography variant="caption">
          Pending: <strong>{totals.tasks_pending}</strong>
        </Typography>
        <Typography variant="caption">
          Overdue: <strong>{totals.tasks_overdue}</strong>
        </Typography>
        <Typography variant="caption">
          Messages: <strong>{totals.messages}</strong>
        </Typography>
        <Typography variant="caption">
          Files: <strong>{totals.files}</strong>
        </Typography>
        <Typography variant="caption">
          Hours: <strong>{totals.hours.toFixed(1)}</strong>
        </Typography>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
          Revenue: {fmtMoney(totals.revenue)}
        </Typography>
      </Box>
      <GridPagination />
    </GridFooterContainer>
  );
}

function ClientActivityReport() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('month'));
  const [dateTo, setDateTo] = useState(() => dayjs());
  const [clientFilter, setClientFilter] = useState(null);
  const [staffFilter, setStaffFilter] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, uRes] = await Promise.all([enhancedApi.getClients(), enhancedApi.getUsers()]);
        if (cancelled) return;
        const clients = Array.isArray(cRes.data) ? cRes.data : [];
        setClientOptions(
          clients.map((c) => ({
            id: c.id,
            label: c.display_name ?? c.name ?? `Client ${c.id}`,
          }))
        );
        const uData = uRes.data;
        const userList = Array.isArray(uData) ? uData : uData?.results ?? uData?.users ?? uData?.data ?? [];
        setStaffOptions(
          userList.map((u) => ({
            id: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
          }))
        );
      } catch {
        if (!cancelled) {
          setClientOptions([]);
          setStaffOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reportParams = useMemo(
    () => ({
      date_from: dateFrom.format('YYYY-MM-DD'),
      date_to: dateTo.format('YYYY-MM-DD'),
      ...(clientFilter?.id != null ? { client_id: clientFilter.id } : {}),
      ...(staffFilter?.id != null ? { staff_id: staffFilter.id } : {}),
    }),
    [dateFrom, dateTo, clientFilter, staffFilter]
  );

  const runReport = useCallback(() => {
    setLoading(true);
    setError(null);
    setHasRun(true);
    enhancedApi
      .getClientActivityReport(reportParams)
      .then((res) => setRawData(res.data))
      .catch(() => {
        setError('Could not load client activity. The report API may not be available yet.');
        setRawData(null);
      })
      .finally(() => setLoading(false));
  }, [reportParams]);

  const rows = useMemo(() => normalizeRows(rawData ?? {}), [rawData]);
  const summary = useMemo(() => normalizeSummary(rawData ?? {}, rows), [rawData, rows]);

  const handleExportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await enhancedApi.exportClientActivityCSV(reportParams);
      const blob = res.data;
      triggerBlobDownload(
        blob,
        `client-activity-${reportParams.date_from}-${reportParams.date_to}.csv`
      );
    } catch {
      setError('Export failed. Ensure the export endpoint is configured.');
    } finally {
      setExporting(false);
    }
  };

  const empty = hasRun && !loading && !error && rows.length === 0;
  const showGrid = hasRun && !loading && rows.length > 0;

  const columns = useMemo(
    () => [
      {
        field: 'client_name',
        headerName: 'Client',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.light' }}>
              {initials(params.row.client_name)}
            </Avatar>
            <Link
              component={RouterLink}
              to={`/client/${params.row.client_id}`}
              underline="hover"
              color="primary"
              fontWeight={600}
              onClick={(e) => e.stopPropagation()}
            >
              {params.row.client_name}
            </Link>
          </Box>
        ),
      },
      {
        field: 'client_type',
        headerName: 'Type',
        width: 120,
        renderCell: (p) => (
          <Chip label={cellVal(p) || '—'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
        ),
      },
      { field: 'tasks_done', headerName: 'Tasks Done', width: 110, type: 'number' },
      { field: 'tasks_pending', headerName: 'Tasks Pending', width: 130, type: 'number' },
      {
        field: 'tasks_overdue',
        headerName: 'Tasks Overdue',
        width: 130,
        type: 'number',
        renderCell: (p) => {
          const v = Number(cellVal(p)) || 0;
          return (
            <Typography variant="body2" fontWeight={v > 0 ? 700 : 400} color={v > 0 ? 'error.main' : 'text.primary'}>
              {v}
            </Typography>
          );
        },
      },
      { field: 'messages', headerName: 'Messages', width: 100, type: 'number' },
      { field: 'files', headerName: 'Files', width: 90, type: 'number' },
      { field: 'hours', headerName: 'Hours', width: 100, type: 'number', valueFormatter: (p) => Number(cellVal(p) || 0).toFixed(1) },
      {
        field: 'revenue',
        headerName: 'Revenue',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: (p) => (
          <Typography variant="body2" fontWeight={700} sx={{ width: '100%', textAlign: 'right', color: 'success.main' }}>
            {fmtMoney(cellVal(p))}
          </Typography>
        ),
      },
      {
        field: 'last_activity_at',
        headerName: 'Last Activity',
        width: 140,
        renderCell: (p) => {
          const rawVal = cellVal(p);
          if (!rawVal) return '—';
          const d = dayjs(rawVal);
          if (!d.isValid()) return String(rawVal);
          const days = dayjs().diff(d, 'day');
          const rel = d.fromNow();
          return (
            <Typography variant="body2" color={days > 30 ? 'error.main' : 'text.secondary'} fontWeight={days > 30 ? 600 : 400}>
              {rel}
            </Typography>
          );
        },
      },
      {
        field: 'health_score',
        headerName: 'Health',
        width: 110,
        renderCell: (p) => {
          const sc = cellVal(p);
          const hp = healthChipProps(sc);
          if (sc == null) return <Chip size="small" variant="outlined" label="—" />;
          return <Chip size="small" label={`${hp.label} (${Number(sc).toFixed(0)})`} color={hp.color} />;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        renderCell: (params) => (
          <Box
            className="client-activity-row-actions"
            sx={{
              display: 'flex',
              gap: 0.5,
              opacity: 0,
              transition: 'opacity 0.15s',
              '.MuiDataGrid-row:hover &': { opacity: 1 },
            }}
          >
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/client/${params.row.client_id}`);
              }}
            >
              View Client
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/client/${params.row.client_id}`, { state: { initialTab: 9 } });
              }}
            >
              Timeline
            </Button>
          </Box>
        ),
      },
    ],
    [navigate]
  );

  const summaryAvgChip = avgHealthChipProps(summary.avg_health_score);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Client Activity Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Engagement, tasks, and revenue by client for the selected period.
            </Typography>
          </Box>
          <Button variant="outlined" disabled={exporting || !hasRun} onClick={handleExportCsv}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <DatePicker label="From" value={dateFrom} onChange={(v) => v && setDateFrom(v)} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="To" value={dateTo} onChange={(v) => v && setDateTo(v)} slotProps={{ textField: { size: 'small' } }} />
            <Autocomplete
              sx={{ minWidth: 220 }}
              size="small"
              options={clientOptions}
              value={clientFilter}
              onChange={(_, v) => setClientFilter(v)}
              getOptionLabel={(o) => o?.label ?? ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => <TextField {...params} label="Client (optional)" />}
            />
            <Autocomplete
              sx={{ minWidth: 220 }}
              size="small"
              options={staffOptions}
              value={staffFilter}
              onChange={(_, v) => setStaffFilter(v)}
              getOptionLabel={(o) => o?.label ?? ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => <TextField {...params} label="Staff (optional)" />}
            />
            <Button variant="contained" color="primary" onClick={runReport} disabled={loading}>
              Run Report
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={88} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 1 }} />
          </Box>
        ) : empty ? (
          <Paper
            variant="outlined"
            sx={{ p: 6, textAlign: 'center', borderColor: 'divider', borderStyle: 'dashed', bgcolor: 'background.paper' }}
          >
            <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={600}>
              No client activity for selected period
            </Typography>
          </Paper>
        ) : showGrid ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Clients
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {summary.total_clients ?? rows.length}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'success.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Tasks Completed
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {summary.tasks_completed ?? 0}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'success.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Revenue Generated
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {fmtMoney(summary.revenue_generated)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'info.main' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Files Uploaded
                </Typography>
                <Typography variant="h6" fontWeight={700} color="info.main">
                  {summary.files_uploaded ?? 0}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', borderTop: 4, borderTopColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Avg Health Score
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip size="small" label={summaryAvgChip.label} color={summaryAvgChip.color} variant={summaryAvgChip.variant || 'filled'} />
                </Box>
              </Paper>
            </Box>

            <Paper variant="outlined" sx={{ borderColor: 'divider', overflow: 'hidden' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                disableRowSelectionOnClick
                autoHeight
                slots={{ footer: () => <ReportTotalsFooter rows={rows} /> }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': { borderColor: 'divider', alignItems: 'center', display: 'flex' },
                  '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                  '& .MuiDataGrid-row:hover .client-activity-row-actions': { opacity: 1 },
                }}
              />
            </Paper>
          </>
        ) : !hasRun ? (
          <Typography variant="body2" color="text.secondary">
            Set a date range and filters, then click <strong>Run Report</strong>.
          </Typography>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
}

export default ClientActivityReport;
